import { useQueries, useQuery } from '@tanstack/react-query'
import { eventQueries } from '../../../../../api/events'
import { boardRoomEventQueries } from '../../../../../api/boardroom-events'
import { pollQueries } from '../../../../../api/polls'
import { groupQueries } from '../../../../../api/groups'
import { getUserFullName, getUserInitials } from '../../../../../helpers/user'
import { IUserWithFiles } from '../../../../../interfaces/services/users.interfaces'
import { ISocietyWithFiles } from '../../../../../interfaces/services/societies.interfaces'
import {
  PostAudience,
  PostEventCard,
  PostRow,
} from '../../../../../generated/api/post-row'
import { PostAttachmentFiles } from '../../../AttachmentsContainer/attachmentSeed'
import { PostData } from './post-data'

const ids = (values: (string | undefined)[] | undefined): string[] =>
  (values || []).filter((value): value is string => !!value)

const audienceOf = (postData: PostData, groupNames: string[]): PostAudience => {
  if (postData.accessAdminMember) {
    return { scope: 'admins', groupNames }
  }
  if (postData.accessBoardMember) {
    return { scope: 'board', groupNames }
  }
  if (ids(postData.groupsIds).length > 0) {
    return { scope: 'groups', groupNames }
  }
  return { scope: 'everyone', groupNames: [] }
}

const NO_ACTIONS: PostRow['actions'] = {
  canLike: false,
  canComment: false,
  commentRoles: [],
  canEdit: false,
  canDelete: false,
  canPin: false,
  canReport: false,
}

export const usePreviewPostRow = (
  postData: PostData,
  society: ISocietyWithFiles,
  author: IUserWithFiles | undefined,
  files: PostAttachmentFiles
): PostRow => {
  const eventId = ids(postData.eventsIds)[0]
  const boardroomEventId = ids(postData.boardroomEventsIds)[0]
  const pollId = ids(postData.pollIds)[0]

  const { data: event } = useQuery(eventQueries.getEvent(eventId))
  const { data: boardroomEvent } = useQuery(
    boardRoomEventQueries.getEvent(boardroomEventId)
  )
  const { data: poll } = useQuery(pollQueries.card(pollId))

  const media = ids(postData.media).flatMap((id) =>
    files.media.filter((item) => item.id === id)
  )
  const documents = ids(postData.documentsIds).flatMap((id) =>
    files.documents.filter((item) => item.id === id)
  )

  const groupNames = useQueries({
    queries: ids(postData.groupsIds).map((id) => groupQueries.getGroup(id)),
  }).flatMap((result) => (result.data ? [result.data.name] : []))

  const toEventCard = (
    kind: PostEventCard['kind'],
    source: typeof event | typeof boardroomEvent
  ): PostEventCard[] =>
    source
      ? [
          {
            id: source._id,
            societyId: source.societyId,
            kind,
            cancelled: false,
            title: source.title,
            description: source.description ?? '',
            location: source.location || undefined,
            startDate: new Date(source.startDate).getTime(),
            endDate: new Date(source.endDate).getTime(),
            isAllDay: source.isAllDay === true,
            documents: source.documents ?? [],
            attendance: { enabled: source.attendanceEnabled === true },
          },
        ]
      : []

  return {
    id: '',
    createdAt: Date.parse(postData.createdAt),
    updatedAt: postData.updatedAt ? Date.parse(postData.updatedAt) : 0,
    role: postData.role as PostRow['role'],
    message: postData.message || '',
    generatedMessage: false,
    isAlert: !!postData.isAlert,
    isPinned: false,
    isScheduled: false,
    author: author && {
      id: author._id,
      fullName: getUserFullName(author),
      initials: getUserInitials(author),
      avatarUrl: author.avatar?.url,
    },
    society: {
      id: society._id,
      name: society.name,
      type: society.societyTypeDisplayName,
      coverUrl: society.coverPhoto?.url,
    },
    audience: audienceOf(postData, groupNames),
    media,
    documents,
    resources: [],
    facilities: [],
    contacts: [],
    offers: [],
    events: [
      ...toEventCard('society', event),
      ...toEventCard('boardroom', boardroomEvent),
    ],
    polls: poll ? [poll] : [],
    likes: { count: 0, viewerLiked: false },
    comments: { count: 0, viewerCommented: false },
    reads: { count: 0, visible: false },
    actions: NO_ACTIONS,
  }
}
